import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { Textarea } from '@/components/ui/Textarea';
import { toast } from '@/store/toastStore';
import { MAX_GIFT_CARD_AMOUNT } from '@/lib/giftCards';
import { getErrorMessage } from '@/services/apiClient';
import * as adminSvc from '@/services/admin.service';
import type { GiftCardSettings, GiftCardSettingsUpdate, GiftCardSettingsWithOptions } from '@/services/admin.service';

// ─── Local form state ─────────────────────────────────────────────────────────

/**
 * Amounts are kept as the text typed, so a half-typed value ("12.") survives
 * a re-render; they are parsed on save.
 */
interface FormState {
  presetAmounts: string;
  minAmount: string;
  maxAmount: string;
  redemptionTerms: string;
  quoteItemCostID: number | null;
  onlineSalesEnabled: boolean;
  onlineRedemptionEnabled: boolean;
}

function toFormState(data: GiftCardSettings): FormState {
  return {
    presetAmounts: (data.presetAmounts ?? []).join(', '),
    minAmount: String(Number(data.minAmount) || 0),
    maxAmount: String(Number(data.maxAmount) || 0),
    redemptionTerms: data.redemptionTerms ?? '',
    quoteItemCostID: data.quoteItemCostID > 0 ? data.quoteItemCostID : null,
    onlineSalesEnabled: !!data.onlineSalesEnabled,
    onlineRedemptionEnabled: !!data.onlineRedemptionEnabled,
  };
}

/** A blank field reads as 0 (no limit); anything else must be a number. */
function parseAmount(text: string): number {
  const trimmed = text.trim();
  return trimmed === '' ? 0 : Number(trimmed);
}

function parsePresets(text: string): number[] {
  return text
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '')
    .map(Number);
}

/**
 * Why the form can't be saved, or null. The same rules the API applies
 * (GiftCardSettingsService::problem), checked here so the admin sees them
 * before saving.
 */
function formProblem(form: FormState): string | null {
  const min = parseAmount(form.minAmount);
  const max = parseAmount(form.maxAmount);
  const presets = parsePresets(form.presetAmounts);

  for (const value of [min, max]) {
    if (!Number.isFinite(value) || value < 0 || value > MAX_GIFT_CARD_AMOUNT) {
      return `Enter amounts between 0 and ${MAX_GIFT_CARD_AMOUNT.toLocaleString('en-US')}.`;
    }
  }
  if (max > 0 && min > max) {
    return 'The minimum amount cannot be more than the maximum amount.';
  }
  if (presets.some((p) => !Number.isFinite(p) || p <= 0 || p > MAX_GIFT_CARD_AMOUNT)) {
    return 'Preset amounts must be numbers greater than 0, separated by commas.';
  }
  if (presets.some((p) => p < min || (max > 0 && p > max))) {
    return 'Each preset amount must be within the minimum and maximum.';
  }
  if (form.onlineSalesEnabled && !form.quoteItemCostID) {
    return 'Choose a gift card product before turning on online sales.';
  }
  return null;
}

function toPayload(form: FormState): GiftCardSettingsUpdate {
  return {
    presetAmounts: parsePresets(form.presetAmounts),
    minAmount: parseAmount(form.minAmount),
    maxAmount: parseAmount(form.maxAmount),
    redemptionTerms: form.redemptionTerms,
    quoteItemCostID: form.quoteItemCostID ?? 0,
    onlineSalesEnabled: form.onlineSalesEnabled,
    onlineRedemptionEnabled: form.onlineRedemptionEnabled,
  };
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * Admin > Payments > Gift Card Settings (online gift cards). One row per
 * account: preset amounts, minimum and maximum, redemption terms, the gift
 * card product online sales are recorded against, and the online switches.
 * The minimum and maximum also apply to cards issued at the desk.
 */
export function GiftCardSettingsAdmin() {
  const [settings, setSettings] = useState<GiftCardSettingsWithOptions | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [savedForm, setSavedForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await adminSvc.getGiftCardSettingsWithOptions();
      setSettings(data);
      const next = toFormState(data);
      setForm(next);
      setSavedForm(next);
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to load gift card settings.');
      setLoadError(message);
      toast.error('Failed to load gift card settings', getErrorMessage(err, ''));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = useMemo(
    () => !!form && !!savedForm && JSON.stringify(form) !== JSON.stringify(savedForm),
    [form, savedForm],
  );

  const problem = useMemo(() => (form ? formProblem(form) : null), [form]);

  const productOptions = useMemo(
    () =>
      (settings?.options.products ?? []).map((p) => ({
        value: p.quoteItemCostID,
        label: p.active ? p.name : `${p.name} (inactive)`,
      })),
    [settings],
  );

  function patch(changes: Partial<FormState>) {
    setForm((f) => (f ? { ...f, ...changes } : f));
  }

  async function handleSave() {
    // The button is disabled on a problem; this guards any other path in.
    if (!form || formProblem(form)) return;
    setSaving(true);
    try {
      const updated = await adminSvc.updateGiftCardSettings(toPayload(form));
      setSettings(updated);
      const next = toFormState(updated);
      setForm(next);
      setSavedForm(next);
      toast.success('Gift card settings saved');
    } catch (err) {
      toast.error('Failed to save gift card settings', getErrorMessage(err, ''));
    } finally {
      setSaving(false);
    }
  }

  const header = (
    <PageHeader
      title="Gift Card Settings"
      subtitle="Preset amounts, limits, redemption terms and online gift card sales for this location."
      actions={
        <Button onClick={handleSave} disabled={!dirty || saving || !!problem} loading={saving}>
          Save Settings
        </Button>
      }
    />
  );

  if (loading) {
    return (
      <div className="flex flex-col h-full overflow-hidden p-6">
        <div className="flex-shrink-0">{header}</div>
        <SpinnerOverlay label="Loading gift card settings…" />
      </div>
    );
  }

  if (!form || !settings) {
    return (
      <div className="flex flex-col h-full overflow-hidden p-6">
        <div className="flex-shrink-0">{header}</div>
        <Alert variant="error" title="Could not load gift card settings">
          <p className="mb-3">{loadError ?? 'Please try again.'}</p>
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden p-6">
      <div className="flex-shrink-0">{header}</div>

      <div className="flex-1 overflow-y-auto space-y-4 pb-4">
        {problem && (
          <Alert variant="warning" title="Fix this before saving">
            {problem}
          </Alert>
        )}

        {/* ─── Amounts ───────────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle>Amounts</CardTitle>
          </CardHeader>
          <CardBody className="grid gap-4 sm:grid-cols-3">
            <Input
              label="Preset amounts"
              value={form.presetAmounts}
              placeholder="50, 100, 250"
              hint="Separate amounts with commas."
              inputMode="decimal"
              onChange={(e) => patch({ presetAmounts: e.target.value })}
            />
            <Input
              label="Minimum amount"
              type="number"
              min={0}
              step="0.01"
              value={form.minAmount}
              hint="0 means no minimum."
              onChange={(e) => patch({ minAmount: e.target.value })}
            />
            <Input
              label="Maximum amount"
              type="number"
              min={0}
              step="0.01"
              value={form.maxAmount}
              hint="0 means no maximum. Also applies to cards issued at the desk."
              onChange={(e) => patch({ maxAmount: e.target.value })}
            />
          </CardBody>
        </Card>

        {/* ─── Redemption terms ──────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle>Redemption Terms</CardTitle>
          </CardHeader>
          <CardBody>
            <Textarea
              label="Redemption terms"
              value={form.redemptionTerms}
              maxLength={5000}
              rows={4}
              autoResize
              onChange={(e) => patch({ redemptionTerms: e.target.value })}
            />
          </CardBody>
        </Card>

        {/* ─── Online ────────────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle>Online Gift Cards</CardTitle>
          </CardHeader>
          <CardBody className="space-y-4">
            <Select<number>
              label="Gift card product"
              options={productOptions}
              value={form.quoteItemCostID}
              placeholder={productOptions.length === 0 ? 'No gift card products' : 'Select…'}
              hint="Online sales are recorded against this product. Only gift card products are listed."
              clearable
              onChange={(v) => patch({ quoteItemCostID: v ?? null })}
            />
            <Checkbox
              label="Online sales"
              description="Let anyone buy a gift card for this location online."
              checked={form.onlineSalesEnabled}
              onChange={() => patch({ onlineSalesEnabled: !form.onlineSalesEnabled })}
            />
            <Checkbox
              label="Online claiming and spending"
              description="Let patients claim and spend gift cards online."
              checked={form.onlineRedemptionEnabled}
              onChange={() => patch({ onlineRedemptionEnabled: !form.onlineRedemptionEnabled })}
            />
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
